/**
 * CampaignProgressTracker - Rich visual progress tracker for fundraising campaigns
 * Features: Multi-segment donut, thermometer, milestone bars, toggles for names/amounts/percentages
 */
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Heart, Target, Users, TrendingUp, DollarSign, Flame } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color palettes
const STATUS_COLORS = {
  confirmed: '#10b981',
  completed: '#22c55e',
  paid: '#06b6d4',
  partial: '#f59e0b',
  pending: '#8b5cf6',
  overdue: '#ef4444',
};
const METHOD_COLORS = {
  cash: '#f59e0b',
  bank_transfer: '#6366f1',
  mobile_money: '#10b981',
  card: '#ec4899',
  online: '#06b6d4',
  cheque: '#8b5cf6',
  unspecified: '#94a3b8',
};
const FREQ_COLORS = {
  one_time: '#6366f1',
  monthly: '#10b981',
  quarterly: '#f59e0b',
  weekly: '#ec4899',
  annually: '#06b6d4',
};
const FALLBACK_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

// ===================== ANIMATED NUMBER =====================
const AnimatedNumber = ({ value, prefix = '$', duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const start = display;
    const diff = value - start;
    if (diff === 0) return;
    const startTime = performance.now();
    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + diff * eased));
      if (progress < 1) ref.current = requestAnimationFrame(animate);
    };
    ref.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(ref.current);
  }, [value]);
  return <span>{prefix}{display.toLocaleString()}</span>;
};

// ===================== MULTI-SEGMENT DONUT =====================
const MultiDonut = ({ segments, centerLabel, centerValue, showNames, showAmounts, size = 180 }) => {
  const radius = 65;
  const strokeWidth = 24;
  const cx = 100, cy = 100;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce((s, seg) => s + seg.value, 0);

  let accumulated = 0;
  const arcs = segments.map((seg, i) => {
    const pct = total > 0 ? seg.value / total : 0;
    const dashLen = pct * circumference;
    const dashOff = circumference - (accumulated / (total || 1)) * circumference;
    accumulated += seg.value;
    return { ...seg, pct, dashLen, dashOff, idx: i };
  });

  const [hovered, setHovered] = useState(null);

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 200 200" width={size} height={size} className="drop-shadow-sm">
        <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth} />
        {arcs.map(arc => (
          <circle
            key={arc.idx}
            cx={cx} cy={cy} r={radius}
            fill="none"
            stroke={arc.color}
            strokeWidth={hovered === arc.idx ? strokeWidth + 6 : strokeWidth}
            strokeDasharray={`${arc.dashLen} ${circumference - arc.dashLen}`}
            strokeDashoffset={arc.dashOff}
            strokeLinecap="butt"
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{
              transition: 'stroke-width 0.2s, opacity 0.2s',
              opacity: hovered !== null && hovered !== arc.idx ? 0.4 : 1,
              cursor: 'pointer',
            }}
            onMouseEnter={() => setHovered(arc.idx)}
            onMouseLeave={() => setHovered(null)}
          />
        ))}
        <text x={cx} y={cy - 6} textAnchor="middle" fill="#1e293b" fontSize="22" fontWeight="bold">{centerValue}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fill="#94a3b8" fontSize="10">{centerLabel}</text>
      </svg>

      {(showNames || showAmounts) && (
        <div className="mt-3 w-full space-y-1">
          {arcs.map(arc => (
            <div
              key={arc.idx}
              className={cn("flex items-center justify-between px-2 py-1 rounded text-sm transition-colors",
                hovered === arc.idx ? "bg-slate-100" : ""
              )}
              onMouseEnter={() => setHovered(arc.idx)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: arc.color }} />
                {showNames && <span className="text-slate-700 truncate max-w-[110px]">{arc.name}</span>}
              </div>
              {showAmounts && <span className="font-medium tabular-nums text-slate-600">{arc.displayValue}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ===================== CAMPAIGN THERMOMETER =====================
const CampaignThermometer = ({ raised, goal, displayMode, color = '#10b981' }) => {
  const pct = goal > 0 ? Math.min((raised / goal) * 100, 100) : 0;
  const [animPct, setAnimPct] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setAnimPct(pct), 200);
    return () => clearTimeout(t);
  }, [pct]);

  const fillColor = pct > 90 ? '#ef4444' : pct > 70 ? '#f59e0b' : color;

  return (
    <div className="flex flex-col items-center">
      <div className="text-center mb-2">
        <div className="text-xs text-slate-500 uppercase tracking-wide">Campaign Goal</div>
        <div className="text-lg font-bold text-slate-800">
          {displayMode === 'amounts'
            ? <><AnimatedNumber value={raised} /> <span className="text-sm font-normal text-slate-400">/ ${goal.toLocaleString()}</span></>
            : <>{Math.round(animPct)}%</>
          }
        </div>
      </div>

      <div className="relative w-10 h-44 flex flex-col items-center">
        <div className="relative w-8 h-36 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
          <div
            className="absolute bottom-0 left-0 right-0 rounded-full transition-all duration-1000 ease-out"
            style={{ height: `${animPct}%`, background: `linear-gradient(to top, ${fillColor}, ${fillColor}dd)` }}
          />
          {[25, 50, 75].map(mark => (
            <div key={mark} className="absolute left-0 right-0 flex items-center" style={{ bottom: `${mark}%` }}>
              <div className="w-full border-t border-slate-200/50" />
              <span className="absolute right-[-22px] text-[8px] text-slate-400">{mark}%</span>
            </div>
          ))}
        </div>
        <div
          className="w-14 h-14 rounded-full -mt-2 border-2 flex items-center justify-center shadow-inner"
          style={{ backgroundColor: fillColor + '20', borderColor: fillColor + '40' }}
        >
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: fillColor }}>
            <Flame className="h-5 w-5 text-white" />
          </div>
        </div>
      </div>

      <div className="mt-1 text-xs font-medium" style={{ color: fillColor }}>{Math.round(animPct)}%</div>
    </div>
  );
};

// ===================== MILESTONE BARS =====================
const MilestoneBars = ({ milestones, displayMode, color }) => {
  if (!milestones?.length) return null;
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-slate-600">Milestones</h4>
      {milestones.map((ms, i) => {
        const pct = ms.progress || 0;
        const statusColor = pct >= 100 ? '#10b981' : pct > 0 ? color : '#94a3b8';
        return (
          <div key={ms.id || i} className="space-y-1">
            <div className="flex justify-between items-center text-xs">
              <span className="text-slate-700 font-medium truncate max-w-[200px]">{ms.title}</span>
              <span className="tabular-nums text-slate-500">
                {displayMode === 'amounts' && ms.target_amount > 0
                  ? `$${(ms.current_amount || 0).toLocaleString()} / $${ms.target_amount.toLocaleString()}`
                  : `${Math.round(pct)}%`
                }
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: statusColor }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ===================== MAIN COMPONENT =====================
const CampaignProgressTracker = ({ slug, compact = false }) => {
  const [showNames, setShowNames] = useState(true);
  const [showAmounts, setShowAmounts] = useState(true);
  const [displayMode, setDisplayMode] = useState('amounts'); // 'amounts' | 'percentages'

  const { data: stats, isLoading } = useQuery({
    queryKey: ['campaign-progress-stats', slug],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/campaigns/${slug}/progress-stats`);
      if (!res.ok) throw new Error('Failed to load');
      return res.json();
    },
    refetchInterval: 30000,
    enabled: !!slug,
  });

  const segments = useMemo(() => {
    if (!stats) return { status: [], method: [], frequency: [] };
    const totalPledged = stats.total_pledged || 1;

    const makeSegments = (breakdown, colorMap) =>
      Object.entries(breakdown || {})
        .filter(([, v]) => v.amount > 0 || v.count > 0)
        .sort((a, b) => b[1].amount - a[1].amount)
        .map(([key, val], i) => ({
          name: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          value: val.amount,
          count: val.count,
          color: colorMap[key] || FALLBACK_COLORS[i % FALLBACK_COLORS.length],
          displayValue: displayMode === 'amounts'
            ? `$${val.amount.toLocaleString()}`
            : `${totalPledged > 0 ? ((val.amount / totalPledged) * 100).toFixed(1) : '0.0'}%`,
        }));

    return {
      status: makeSegments(stats.status_breakdown, STATUS_COLORS),
      method: makeSegments(stats.method_breakdown, METHOD_COLORS),
      frequency: makeSegments(stats.frequency_breakdown, FREQ_COLORS),
    };
  }, [stats, displayMode]);

  if (isLoading) {
    return (
      <Card><CardContent className="p-8"><div className="flex items-center justify-center gap-3">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-slate-500">Loading progress...</span>
      </div></CardContent></Card>
    );
  }

  if (!stats) return null;

  const primaryColor = stats.primary_color || '#10b981';

  if (compact) {
    const pct = stats.percentage || 0;
    return (
      <Card data-testid="campaign-progress-compact">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative w-16 h-16">
              <svg viewBox="0 0 100 100" width={64} height={64}>
                <circle cx={50} cy={50} r={40} fill="none" stroke="#f1f5f9" strokeWidth={8} />
                <circle cx={50} cy={50} r={40} fill="none" stroke={primaryColor} strokeWidth={8}
                  strokeDasharray={`${(pct / 100) * 251.3} ${251.3 - (pct / 100) * 251.3}`}
                  strokeDashoffset={251.3 / 4} strokeLinecap="round"
                  className="transition-all duration-1000"
                  transform="rotate(-90 50 50)"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-xs font-bold">{Math.round(pct)}%</div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-slate-900 truncate text-sm">{stats.campaign_name}</h3>
              <div className="flex gap-3 mt-1 text-xs text-slate-500">
                <span>${(stats.raised || 0).toLocaleString()} raised</span>
                <span>{stats.pledge_count} pledges</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="campaign-progress-tracker">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Heart className="h-5 w-5" style={{ color: primaryColor }} />
            Campaign Progress
          </CardTitle>
          <div className="flex items-center gap-4 flex-wrap">
            {/* Amount / Percentage toggle */}
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              <button
                data-testid="campaign-toggle-amounts"
                onClick={() => setDisplayMode('amounts')}
                className={cn("px-3 py-1 text-xs font-medium rounded-md transition-all",
                  displayMode === 'amounts' ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                )}
              >Amounts</button>
              <button
                data-testid="campaign-toggle-percentages"
                onClick={() => setDisplayMode('percentages')}
                className={cn("px-3 py-1 text-xs font-medium rounded-md transition-all",
                  displayMode === 'percentages' ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"
                )}
              >Percentages</button>
            </div>
            {/* Names toggle */}
            <div className="flex items-center gap-1.5">
              <Switch id="camp-names" data-testid="campaign-toggle-names" checked={showNames} onCheckedChange={setShowNames} className="scale-75" />
              <Label htmlFor="camp-names" className="text-xs text-slate-500 cursor-pointer">Names</Label>
            </div>
            {/* Amounts legend toggle */}
            <div className="flex items-center gap-1.5">
              <Switch id="camp-vals" data-testid="campaign-toggle-values" checked={showAmounts} onCheckedChange={setShowAmounts} className="scale-75" />
              <Label htmlFor="camp-vals" className="text-xs text-slate-500 cursor-pointer">Values</Label>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Donut: By Pledge Status */}
          <div className="flex flex-col items-center">
            <h3 className="text-sm font-medium text-slate-600 mb-3">By Status</h3>
            {segments.status.length > 0 ? (
              <MultiDonut
                segments={segments.status}
                centerLabel="Pledged"
                centerValue={displayMode === 'amounts' ? `$${(stats.total_pledged || 0).toLocaleString()}` : `${stats.percentage}%`}
                showNames={showNames}
                showAmounts={showAmounts}
                size={170}
              />
            ) : (
              <div className="w-full max-w-[170px] h-[170px] rounded-full bg-slate-100 flex items-center justify-center">
                <span className="text-xs text-slate-400">No pledges</span>
              </div>
            )}
          </div>

          {/* Thermometer: Overall Goal */}
          <div className="flex flex-col items-center">
            <h3 className="text-sm font-medium text-slate-600 mb-3">Goal Progress</h3>
            <CampaignThermometer
              raised={stats.raised || 0}
              goal={stats.goal || 1}
              displayMode={displayMode}
              color={primaryColor}
            />
          </div>

          {/* Donut: By Frequency */}
          <div className="flex flex-col items-center">
            <h3 className="text-sm font-medium text-slate-600 mb-3">By Frequency</h3>
            {segments.frequency.length > 0 ? (
              <MultiDonut
                segments={segments.frequency}
                centerLabel="Pledges"
                centerValue={stats.pledge_count}
                showNames={showNames}
                showAmounts={showAmounts}
                size={170}
              />
            ) : (
              <div className="w-full max-w-[170px] h-[170px] rounded-full bg-slate-100 flex items-center justify-center">
                <span className="text-xs text-slate-400">No data</span>
              </div>
            )}
          </div>
        </div>

        {/* Milestones */}
        {stats.milestones?.length > 0 && (
          <div className="mt-6 pt-4 border-t">
            <MilestoneBars milestones={stats.milestones} displayMode={displayMode} color={primaryColor} />
          </div>
        )}

        {/* Summary badges */}
        <div className="mt-6 pt-4 border-t flex flex-wrap gap-3 justify-center">
          <Badge variant="outline" className="text-xs gap-1.5 px-3 py-1">
            <Users className="h-3 w-3" />
            {stats.pledge_count} Pledges
          </Badge>
          <Badge variant="outline" className="text-xs gap-1.5 px-3 py-1" style={{ borderColor: primaryColor + '60', color: primaryColor }}>
            <DollarSign className="h-3 w-3" />
            ${(stats.total_pledged || 0).toLocaleString()} Pledged
          </Badge>
          <Badge variant="outline" className="text-xs gap-1.5 px-3 py-1 border-green-200 text-green-700">
            <Target className="h-3 w-3" />
            ${(stats.total_paid || 0).toLocaleString()} Paid
          </Badge>
          {stats.goal > 0 && (
            <Badge variant="outline" className="text-xs gap-1.5 px-3 py-1 border-amber-200 text-amber-700">
              <TrendingUp className="h-3 w-3" />
              ${(stats.goal - stats.raised).toLocaleString()} Remaining
            </Badge>
          )}
        </div>

        {/* Top donors */}
        {stats.top_donors?.length > 0 && showNames && (
          <div className="mt-4 pt-3 border-t">
            <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Top Pledgers</h4>
            <div className="flex flex-wrap gap-2">
              {stats.top_donors.map((d, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs bg-slate-50 rounded-full px-3 py-1">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ background: primaryColor }}>
                    {d.name[0]?.toUpperCase()}
                  </div>
                  <span className="text-slate-700">{d.name}</span>
                  {showAmounts && <span className="text-slate-500">${d.amount.toLocaleString()}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CampaignProgressTracker;
